import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, Outlet } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import Navbar from './components/Navbar';
import HomePage from './pages/HomePage';
import RaceListPage from './pages/RaceListPage';
import RaceDetailPage from './pages/RaceDetailPage';
import AdminPage from './pages/AdminPage';
import LivePage from './pages/LivePage';
import DriverLivePage from './pages/DriverLivePage';
import PrivacyPage from './pages/PrivacyPage';
import RaffleEntryPage from './pages/RaffleEntryPage';
import AdminRafflesPage from './pages/AdminRafflesPage';
import RaffleWallPage from './pages/RaffleWallPage';
import DriversPage from './pages/DriversPage';
import PitWallPage from './pages/PitWallPage';
import PitWallPageV2 from './pages/PitWallPageV2';
import PitWallPageV3 from './pages/PitWallPageV3';

const NOTICE_KEY = 'f1tips_notice_miami_dismissed';
const NOTICE_EXPIRES = new Date('2026-05-03T17:00:00Z'); // 19:00 CEST

function UrgentNotice() {
  const [dismissed, setDismissed] = useState(() => !!localStorage.getItem(NOTICE_KEY));
  if (dismissed || Date.now() >= NOTICE_EXPIRES) return null;
  return (
    <div style={{
      background: '#7c2d12', borderBottom: '1px solid #9a3412',
      padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
    }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: '#fed7aa', flex: 1, textAlign: 'center' }}>
        ⚠ Race start moved to 19:00 today
      </span>
      <button
        onClick={() => { localStorage.setItem(NOTICE_KEY, '1'); setDismissed(true); }}
        style={{ background: 'none', border: 'none', color: '#fb923c', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 4px', flexShrink: 0 }}
        aria-label="Dismiss"
      >×</button>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: 960,
    margin: '0 auto',
    padding: '0 12px 40px',
  },
  footer: {
    textAlign: 'center',
    padding: '24px 0 32px',
    borderTop: '1px solid #222',
    marginTop: 40,
  },
};

function MainLayout() {
  return (
    <>
      <UrgentNotice />
      <Navbar />
      <div style={styles.container}>
        <Outlet />
        <footer style={styles.footer}>
          {/* Row 1 — social + privacy */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20 }}>
            <a href="https://www.facebook.com/p/Formel-1-Bryggeriet-%C3%85ngkvarn-61558092965016/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" style={{ color: '#383838', lineHeight: 0, transition: 'color 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.color = '#555'}
              onMouseLeave={e => e.currentTarget.style.color = '#383838'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.235 2.686.235v2.97h-1.513c-1.491 0-1.956.93-1.956 1.884v2.25h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/>
              </svg>
            </a>
            <a href="https://www.instagram.com/formel1_bryggerietangkvarn/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" style={{ color: '#383838', lineHeight: 0, transition: 'color 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.color = '#555'}
              onMouseLeave={e => e.currentTarget.style.color = '#383838'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <div style={{ width: 1, height: 12, background: '#222' }} />
            <Link to="/privacy" style={{ color: '#444', fontSize: 12, textDecoration: 'none', fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: '0.08em', transition: 'color 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.color = '#666'}
              onMouseLeave={e => e.currentTarget.style.color = '#444'}
            >
              Privacy Policy
            </Link>
          </div>

          {/* Row 2 — legal + attribution */}
          <div style={{ borderTop: '1px solid #161616', marginTop: 14, paddingTop: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
            <p style={{ fontSize: 11, color: '#333', margin: 0 }}>
              © {new Date().getFullYear()} Bryggeriet Ångkvarn · Not affiliated with Formula 1®, FOM or FIA
            </p>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#2a2a2a', margin: 0 }}>
              Data ·{' '}
              <a href="https://openf1.org" target="_blank" rel="noopener noreferrer" style={{ color: '#2a2a2a', textDecoration: 'none', transition: 'color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.color = '#555'}
                onMouseLeave={e => e.currentTarget.style.color = '#2a2a2a'}
              >OpenF1</a>
              {' · '}
              <a href="https://jolpi.ca" target="_blank" rel="noopener noreferrer" style={{ color: '#2a2a2a', textDecoration: 'none', transition: 'color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.color = '#555'}
                onMouseLeave={e => e.currentTarget.style.color = '#2a2a2a'}
              >Jolpica</a>
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Full-viewport route — no navbar, no container */}
        <Route path="/pitwall" element={<PitWallPage />} />
        {/* Experimental: live track map spike, kept separate from /pitwall until proven live */}
        <Route path="/pitwall2" element={<PitWallPageV2 />} />
        <Route path="/pitwall3" element={<PitWallPageV3 />} />
        <Route path="/rafflewall/:id" element={<RaffleWallPage />} />

        {/* Standard layout routes */}
        <Route element={<MainLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/races" element={<RaceListPage />} />
          <Route path="/races/:id" element={<RaceDetailPage />} />
          <Route path="/races/:id/driver-live" element={<DriverLivePage />} />
          <Route path="/drivers" element={<DriversPage />} />
          <Route path="/live" element={<LivePage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin/raffles" element={<AdminRafflesPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/raffles/:id" element={<RaffleEntryPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
      <Analytics />
      <SpeedInsights />
    </BrowserRouter>
  );
}
